import type { ShotHandle } from '@/capture/captureSession';
import { errorCue, stepCue } from '@/capture/guidance/guidanceCopy';
import { useGuidance, useGuidanceCue } from '@/capture/guidance/guidanceStore';
import { hapticGoodShot, hapticRetake } from '@/capture/haptics';
import { track } from '@/flow/instrument';
import { type MotionPermission, motionPermissionNeedsGesture, requestMotionPermission } from '@/capture/imu';
import { useLowData } from '@/capture/lowData';
import type { GeoFix } from '@/capture/shotGeo';
import { parseStamp, previewModel } from '@/capture/stampPreview';
import type { StillKind } from '@/capture/thresholds';
import { useFramingHints } from '@/capture/useFramingHints';
import { useShotGeoWatch } from '@/capture/GeoWatchContext';
import { useLatestFix } from '@/capture/useLatestFix';
import { useLevel } from '@/capture/useLevel';
import { useNow } from '@/capture/useNow';
import { useSideways } from '@/capture/useSideways';
import { useStampMosaic, useStampTile } from '@/capture/useStampTile';
import { useGridOn } from '@/capture/viewfinderPrefs';
import { useT } from '@/i18n/runtime';
import { type FaceGateResult, assessFaceVisible } from '@/media/faceVisible';
import { type QualityVerdict, assessCaptureQuality } from '@/media/imageQuality';
import { useShotCamera } from '@/media/CaptureCameraContext';
import type { CaptureFacing } from '@/media/useCaptureCamera';
import { useCaptureIntegrity } from '@/media/useCaptureIntegrity';
import { useCaptureStore } from '@/store/captureStore';
import { defaultFacing, isIdDocPrompt, isSelfiePrompt } from '@/store/selectors';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { type ShotProps, cameraBadgeKey } from './ShotClip';
import { ShotHeader, maskingNotApplicable, refusalKeys } from './ShotHeader';
import { useDemoAutoPlay } from './demos/demoStore';
import { Affirmation, useAffirmation } from './components/Affirmation';
import { AppBar } from './components/AppBar';
import { BlockedCameraCard, blockedCameraWords } from './components/BlockedCameraCard';
import { Button } from './components/Button';
import { BarButton, BarSlot, CaptureBar, Shutter } from './components/CaptureBar';
import { FramingHintLine } from './components/FramingHint';
import { GridOverlay } from './components/GridOverlay';
import { GuideFrame } from './components/GuideFrame';
import { Icon } from './components/Icon';
import { LevelIndicator } from './components/LevelIndicator';
import { distanceWordsFor } from './components/LocationCard';
import { SavedWillSend } from './components/SavedWillSend';
import { StampMapSheet } from './components/StampMapSheet';
import { CameraStalled } from './components/CameraStalled';
import { StampPreviewCard } from './components/StampPreviewCard';
import { useHelp, useHelpStep } from './help/HelpContext';

interface Shot {
  blob: Blob;
  url: string;
  verdict: QualityVerdict;
  face: FaceGateResult | null;
  /** The live view was mirrored when this was taken: the preview is too, so the picture doesn't flip at the shutter. */
  mirrored: boolean;
}

const IMG_FILL: CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' };
const IMG_FILL_MIRRORED: CSSProperties = { ...IMG_FILL, transform: 'scaleX(-1)' };
const RP_NAME = import.meta.env.VITE_RP_NAME || 'Site verification';
/**
 * The document shot frames the WHOLE card: an ID-1-shaped box (85.6 × 54 mm) centred in the clear middle of the viewfinder
 * (`.sv-vf-mid`), as wide as fits — never under the instruction card or the hints (FSD-20 D9). The selfie oval does NOT
 * sit there: it is sized from the whole viewfinder in its own layer (`.vp-guide-layer`) and shows through the glass
 * cards (FSD-20 D14). The geometry is in `svapp.css`, NOT inline: an inline container-unit value is dropped whole by a
 * browser without container units (iOS < 16), which left a 96 × 62 px corner box on iOS 14/15.
 */
const DOC_CARD_FRAME: CSSProperties = {};
const SELFIE_FACE_FRAME: CSSProperties = {};

export function kindFor(promptKey: string): StillKind {
  if (isSelfiePrompt(promptKey)) return 'SV_SELFIE';
  if (isIdDocPrompt(promptKey)) return 'SV_ID_DOC';
  return 'SV_STILL';
}

/**
 * A STILL shot — the SelfieCaptureCore pattern from vCIP_droom_4.0 (capture-only by construction,
 * correct-before-shooting, calm retake, on-device quality gate as the UX arbiter) applied to
 * premises stills, the representative selfie (front camera, face-visible gate where the browser
 * offers one, honest NOT_EVALUATED where it does not) and the identity document (rear camera, the
 * full-card box, the stricter floor + the glare gate, the DEC-055 storage words — never a refusal to
 * capture). One JPEG = one chunk = a one-link chain — the document rides the SAME chain as every still.
 *
 * Layout (SPEC_CAPTURE_UI_20260925): the viewfinder fills the screen; the instruction card and the
 * progress strip ride over its top, the location card and the hint over its bottom; every control
 * is in the one bottom bar. Premises stills (`SV_STILL`) get the rule-of-thirds grid + the level
 * (§D of the stamp spec): grid by default, level only when motion is granted or not required —
 * asked for on the "turn on the camera" tap, the gesture iOS needs; denied ⇒ grid only, never faked.
 */
export function ShotStill(p: ShotProps): JSX.Element {
  const { t, locale } = useT();
  const { session, shot: script } = p;
  const kind = kindFor(script.promptKey);
  const selfie = kind === 'SV_SELFIE';
  const doc = kind === 'SV_ID_DOC';
  const sideways = useSideways();
  const premises = kind === 'SV_STILL';
  // G1 (RCA 2026-11-03): the flow's ONE camera session — this screen re-mounts per shot, the camera does not.
  const cam = useShotCamera();
  // FSD-20 D15: most desktop webcams report no `facingMode`, so the active facing is unknown there. The view mirrors
  // when the camera says it faces the user OR, when it says nothing, when the front camera is what was asked for.
  const mirror = (cam.activeFacing ?? cam.requestedFacing) === 'user';
  const [facing, setFacing] = useState<CaptureFacing>(defaultFacing(script.promptKey));
  const [shot, setShot] = useState<Shot | null>(null);
  const [assessing, setAssessing] = useState(false);
  const [sealing, setSealing] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // The session's motion permission, mirrored into state so the level can react to the decision.
  // ImuTracer's default 'unsupported' doubles as "not decided yet on this session".
  const [motion, setMotion] = useState<MotionPermission>(session.imu.permission);
  const [gridOn, setGridOn] = useGridOn();
  // While the accepted photo is being chained: the fix its chunk binds (`handle.boundFix`), so the card
  // freezes to exactly that. `undefined` = nothing in flight; null = in flight with no fix held at start.
  const [inFlight, setInFlight] = useState<GeoFix | null | undefined>(undefined);
  const disposed = useRef(false);
  const urls = useRef<string[]>([]);
  const permissions = useCallback(
    () => ({ camera: cam.phase === 'live', microphone: false, geolocation: p.geoPermission, motion: session.imu.permission }),
    [cam.phase, p.geoPermission, session.imu.permission],
  );
  useCaptureIntegrity(cam.getStream, cam.phase === 'live' && !shot, permissions);

  const live = cam.phase === 'live';
  const motionOk = motion === 'granted' || motion === 'not-required';
  const overlaysOn = premises && gridOn && live && !shot;
  const level = useLevel(overlaysOn && motionOk);
  const fix = useLatestFix();
  // G2 (RCA 2026-11-03): the capture's ONE shared watch; the card freezes to the shot's bound fix while it is in flight.
  const watch = useShotGeoWatch();
  // The exchange's `summary.stamp` (contract v0.1.12) — null on an older be ⇒ the no-anchor card form.
  const summary = useCaptureStore((s) => s.summary);
  const stamp = parseStamp(summary?.stamp);
  const now = useNow();
  const cardModel = previewModel(fix, p.geoPermission, stamp, now, inFlight ?? null, watch.error);
  // Low-data mode (SPEC_CAPTURE_UX_R2 §7.4): the thumbnail waits for a "Show map" tap; nothing else changes.
  const lowData = useLowData();
  const [mapAsked, setMapAsked] = useState(false);
  const tileWanted = cardModel.kind === 'fix' && stamp !== null && stamp.liveCard === 'COORDINATES';
  // The map thumbnail: the be's own tile under the fix the card shows (frozen with it during a shot); only where the
  // card shows coordinates on a be that has the route — the be's 204 OFF / HIDDEN decides the rest.
  const tile = useStampTile(cardModel.kind === 'fix' ? { lat: cardModel.lat, lng: cardModel.lng } : null, tileWanted && (!lowData.on || mapAsked), stamp?.anchor ?? null);
  // Round 2: the help layer (why this shot / skip with a reason on an optional shot / ask for a call / recovery), the
  // advisory framing hints before the shutter, the ~700 ms affirmation after a good one, and the haptics.
  const help = useHelp();
  const framing = useFramingHints(cam.getVideo, cam.phase === 'live' && !shot && !assessing, selfie);
  const affirm = useAffirmation();
  // Round 2 phase 3: the spoken cue for this step (or for the error card that replaced it), and the §4
  // demonstration the first time this case meets this shot type.
  const guidance = useGuidance();
  // The expanded map (tap on the thumbnail): the 3×3 ring around the drawn tile, over the viewfinder only — the bar
  // below stays tappable, so the shot never waits for a map.
  const [mapOpen, setMapOpen] = useState(false);
  // FSD-20 D7: every shot starts with the location card as one row; "Details" opens the full lines (the screen remounts per shot).
  const [locOpen, setLocOpen] = useState(false);
  const [docTipsOpen, setDocTipsOpen] = useState(false);
  const mosaic = useStampMosaic(mapOpen && tile.status === 'ready' ? tile.ref : null, stamp?.anchor ?? null);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      for (const u of urls.current) URL.revokeObjectURL(u);
      urls.current = [];
    };
  }, []);

  useEffect(() => {
    if (shot) return;
    if (cam.phase === 'primer' && cam.preGranted) cam.start(facing, { profile: 'still', audio: false });
  }, [shot, cam.phase, cam.preGranted, cam.start, facing]);

  // G1: a shot that mounts onto the flow's running session asks for ITS profile and lens — a no-op, a quality switch
  // on the running track, or (another lens) the one re-acquisition that is really needed.
  const camRunning = cam.phase === 'live' || cam.phase === 'stalled';
  useEffect(() => {
    if (camRunning) cam.start(facing, { profile: 'still', audio: false });
  }, [camRunning, cam.start, facing]);

  /** Decide motion once per session. Must be called synchronously inside a gesture where iOS requires one. */
  const decideMotion = useCallback((): void => {
    if (!premises || motion !== 'unsupported') return;
    void requestMotionPermission().then((granted) => {
      session.imu.permission = granted;
      if (!disposed.current) setMotion(granted);
    });
  }, [premises, motion, session]);

  // Android / desktop fire motion without asking — decide from the effect so an auto-started camera still gets the level.
  useEffect(() => {
    if (premises && motion === 'unsupported' && !motionPermissionNeedsGesture()) decideMotion();
  }, [premises, motion, decideMotion]);

  const turnOn = (): void => {
    decideMotion();
    // SPEC_CAPTURE_UX_R2 §3.1 / SPEC_AUDIO_GUIDANCE §3.1: THIS is the unlock gesture the autoplay policies need —
    // the player's one element is created inside this handler, and the step's cue speaks from here on.
    guidance.unlock();
    cam.start(facing, { profile: 'still', audio: false });
  };

  const toggleGrid = (): void => {
    if (!gridOn) decideMotion();
    setGridOn(!gridOn);
  };

  const flip = (): void => {
    const next: CaptureFacing = facing === 'user' ? 'environment' : 'user';
    setFacing(next);
    cam.start(next, { profile: 'still', audio: false });
  };

  const capture = async (): Promise<void> => {
    if (assessing) return;
    setAssessing(true);
    setNote(null);
    try {
      const blob = await cam.captureFrame();
      if (disposed.current) return;
      if (!blob) {
        setNote('sv.capture.camera.notready');
        return;
      }
      const verdict = await assessCaptureQuality(blob, kind);
      const face = selfie && verdict.ok ? await assessFaceVisible(blob) : null;
      if (disposed.current) return;
      const url = URL.createObjectURL(blob);
      urls.current.push(url);
      // the blob stays as the camera took it (the evidence); only its preview follows the mirrored live view
      setShot({ blob, url, verdict, face, mirrored: mirror });
      if (verdict.ok && !(face?.evaluated && !face.ok)) {
        affirm.trigger();
        hapticGoodShot();
      }
    } finally {
      if (!disposed.current) setAssessing(false);
    }
  };

  const retake = (): void => {
    if (shot) {
      URL.revokeObjectURL(shot.url);
      urls.current = urls.current.filter((u) => u !== shot.url);
    }
    setShot(null);
    hapticRetake();
  };

  const accept = async (): Promise<void> => {
    if (!shot || !shot.verdict.ok || sealing) return;
    if (shot.face?.evaluated && !shot.face.ok) return;
    setSealing(true);
    try {
      await session.ensureSigningKey(RP_NAME);
      const handle: ShotHandle = await session.beginShot(script);
      if (!disposed.current) setInFlight(handle.boundFix);
      await handle.appendChunk(shot.blob, new Date().toISOString());
      await handle.seal({ mimeType: shot.blob.type || 'image/jpeg', durationS: null });
    } finally {
      if (!disposed.current) {
        setSealing(false);
        setInFlight(undefined);
      }
    }
  };

  const camBlocked = ['denied', 'os-denied', 'no-device', 'webview', 'unsupported'].includes(cam.phase);
  const faceBlocked = !!shot?.face?.evaluated && !shot.face.ok;
  // §3.3 — an error card speaks its OWN words; otherwise the step speaks the server's prompt, verbatim (§3.1).
  const blockedWords = blockedCameraWords(t, cam.phase, cam.detail);
  useGuidanceCue(
    camBlocked
      ? errorCue('camera', [blockedWords.title, blockedWords.body], locale)
      : p.refusal
        ? errorCue(`refused.${p.refusal.code}`, [t(refusalKeys(p.refusal.code, script).title), t(refusalKeys(p.refusal.code, script).body)], locale)
        : stepCue(script, locale),
  );
  useDemoAutoPlay(script, !camBlocked && !shot);
  useHelpStep({
    screen: 'capture',
    shot: script,
    optional: !script.mandatory,
    onSkip: (reason) => {
      useCaptureStore.getState().markSkipped(script.shotId, reason);
      session.skipShot(script);
    },
    tenantName: p.tenant.name,
    blocked: camBlocked ? 'camera' : p.geoPermission === 'DENIED' ? 'location' : null,
  });
  const hint = ((): { text: string; alert: boolean; good: boolean } => {
    if (shot) {
      if (!shot.verdict.ok) return { text: t(shot.verdict.messageKey ?? 'sv.gate.still.unreadable'), alert: true, good: false };
      if (faceBlocked) return { text: t('sv.capture.face.none'), alert: true, good: false };
      if (sealing) return { text: t('sv.capture.queued'), alert: false, good: false };
      if (selfie && shot.face && !shot.face.evaluated) return { text: t('sv.capture.face.notEvaluated'), alert: false, good: true };
      return { text: t('sv.capture.preview.ok'), alert: false, good: true };
    }
    if (assessing) return { text: t('sv.capture.assessing'), alert: false, good: false };
    if (note) return { text: t(note), alert: false, good: false };
    switch (cam.phase) {
      case 'checking': return { text: t('sv.capture.camera.checking'), alert: false, good: false };
      case 'primer': return { text: t('sv.capture.camera.primer'), alert: false, good: false };
      case 'starting': return { text: t('sv.capture.camera.starting'), alert: false, good: false };
      case 'stalled': return { text: '', alert: false, good: false }; // G3: the CameraStalled card speaks
      // FSD-20 D6: on a premises still a framing correction replaces the generic line-up sentence (one row, not two);
      // the selfie instruction is specific and always stays. FSD-20 D9 (OD-6): on the ID document the document tips
      // card is on screen the whole time the camera is live and says the same thing, so the over-video line steps
      // aside and the guide box gets its room. Gate reasons after the shutter are never affected (the `shot` branch above).
      case 'live': return { text: selfie ? t('sv.capture.hint.selfie') : doc ? '' : framing ? '' : t('sv.capture.hint.lineup'), alert: false, good: false };
      default: return { text: '', alert: false, good: false };
    }
  })();
  const videoStyle: CSSProperties = { objectFit: 'cover', ...(mirror ? { transform: 'scaleX(-1)' } : {}) };

  const pills = (
    <div className="sv-pills">
      <span className="sv-pill" data-testid="camera-badge">
        <Icon name="camera" size={12} />
        {shot ? t('sv.capture.camera.badge.preview') : t(cameraBadgeKey(cam.activeFacing))}
      </span>
      {premises && !shot ? (
        <button
          type="button"
          className="sv-pill"
          aria-pressed={gridOn}
          aria-label={gridOn ? t('sv.capture.grid.hide') : t('sv.capture.grid.show')}
          onClick={toggleGrid}
          data-testid="grid-toggle"
        >
          <Icon name="grid" size={12} />
          {t('sv.capture.grid.label')}
        </button>
      ) : null}
    </div>
  );
  const decide = !!shot || camBlocked || cam.phase === 'primer';
  // Skip shows exactly where the bar's Skip used to: camera live, nothing to decide (FSD-20 Addendum C)
  const skip = !decide && !script.mandatory ? () => session.skipShot(script) : null;
  const header = <ShotHeader shot={script} stepIndex={p.stepIndex} stepTotal={p.stepTotal} refusal={p.refusal} recaptureOnly={p.recaptureOnly} right={pills} onSkip={skip} />;
  // FSD-20 D9: with the camera live the three tips fold behind "Details" ("Show me" and the prompt say the same over
  // the video) so the viewfinder keeps a usable box. Blocked ⇒ all open. The DEC-055 storage words are never folded:
  // on this step they sit beside the shutter, in place of the generic footer (OD-6), read right before the photo.
  // FSD-20 D9 review: folded is never EMPTY. The one-line framing guidance ("Keep the whole document inside the box,
  // flat, with no glare.") is always on screen, without a tap: it heads the card while the camera is live (the generic
  // title becomes the card's accessible name), and sits under the title in the blocked column.
  const tipsFolded = !camBlocked && !docTipsOpen;
  // The DEC-055 / §14f storage words: ONE element, in one place. Upright it sits beside the shutter in the bar (OD-6).
  // Held sideways the bar is a ~116 px rail, where these four lines ran over the shutter and the language buttons
  // (F-S8): there they move to the viewfinder's bottom-right stack — still beside the shutter, on glass like every line
  // over the camera — or, with the camera blocked (no viewfinder), into the tips card. Never folded, never dropped.
  const storedNote = doc ? (
    <span className="sv-doc-stored" role="note" data-testid="doc-stored">
      {/* §14f: the be masks an ID_DOCUMENT photo before it stores it — and only that key (an older be's REP_ID is
          stored as taken), so only that key is told so; F-S7: never on a step whose policy the be does not mask */}
      <Icon name="lock" size={12} />{' '}
      {t(
        script.promptKey !== 'ID_DOCUMENT'
          ? 'sv.capture.doc.stored'
          : maskingNotApplicable(script)
            ? 'sv.capture.doc.storedPolicy'
            : 'sv.capture.doc.storedMasked',
      )}
    </span>
  ) : null;
  const blockedColumn = camBlocked && !shot;
  const framingLine = (
    <span className="sv-doc-framing" data-testid="doc-framing">
      {t('sv.capture.hint.doc')}
    </span>
  );
  const docGuide = doc ? (
    <div className="sv-guide card" role="group" aria-label={t('sv.capture.doc.guide.title')} data-testid="doc-guidance" data-folded={tipsFolded ? 'true' : 'false'}>
      {camBlocked ? <div className="pt"><Icon name="doc" size={14} /> {t('sv.capture.doc.guide.title')}</div> : null}
      <div className="pt">
        {camBlocked ? framingLine : (
          <>
            <Icon name="doc" size={14} /> {framingLine}
          </>
        )}
        {!camBlocked ? (
          <button type="button" className="sv-guide-toggle" aria-expanded={!tipsFolded} aria-controls="sv-doc-tips" onClick={() => setDocTipsOpen((o) => !o)} data-testid="doc-tips-toggle">
            <span>{tipsFolded ? t('sv.action.details') : t('sv.action.detailsHide')}</span>
            <Icon name="chevron" size={12} />
          </button>
        ) : null}
      </div>
      <ul className="sv-guide-list" id="sv-doc-tips" hidden={tipsFolded}>
        <li>{t('sv.capture.doc.guide.frame')}</li>
        <li>{t('sv.capture.doc.guide.glare')}</li>
        <li>{t('sv.capture.doc.guide.lens')}</li>
      </ul>
      {sideways && blockedColumn ? storedNote : null}
    </div>
  ) : null;
  // FSD-20 Addendum C: shooting, the bar keeps only what must stay beside the shutter — the ID step's DEC-055 storage
  // words; the generic footer is said in the decide states only. "Saved, will send" sits over the camera with the other
  // status lines (in the bar only when there is no viewfinder: the blocked column).
  const caption = (
    <>
      {blockedColumn ? <SavedWillSend /> : null}
      {doc ? (sideways ? null : storedNote) : decide ? (
        <span>{t('sv.capture.footer')}</span>
      ) : null}
    </>
  );

  return (
    <div className="app-view sv-cam" data-testid="still-screen" data-still-kind={kind}>
      <AppBar tenant={p.tenant} subtitle={t('sv.subtitle')} />

      {blockedColumn ? (
        <div className="sv-cam-blocked">
          {header}
          <BlockedCameraCard cam={cam} />
          {docGuide}
        </div>
      ) : (
        <>
          <div className="viewport sv-vf" data-testid="still-viewport">
            {shot ? (
              <img src={shot.url} alt="" style={shot.mirrored ? IMG_FILL_MIRRORED : IMG_FILL} />
            ) : cam.phase === 'starting' || cam.phase === 'live' || cam.phase === 'stalled' ? (
              <video ref={cam.attachVideo} className="vp-scene" autoPlay playsInline muted style={videoStyle} />
            ) : null}
            {overlaysOn ? <GridOverlay /> : null}
            {overlaysOn && level ? <LevelIndicator reading={level} /> : null}
            {!shot && live && selfie ? (
              <div className="vp-guide-layer" data-testid="face-guide-layer">
                <GuideFrame state="neutral" face={SELFIE_FACE_FRAME} />
              </div>
            ) : null}
            <div className="sv-vf-top">{header}</div>
            <div className="sv-vf-mid">
              {!shot && live && doc ? <GuideFrame state="neutral" card={DOC_CARD_FRAME} /> : null}
            <Affirmation show={affirm.show} />
            </div>
            <div className="sv-vf-bottom">
              <SavedWillSend />
              {sideways ? storedNote : null}
              {!doc ? (
                <StampPreviewCard
                  model={cardModel}
                  liveCard={stamp?.liveCard ?? 'COORDINATES'}
                  compact={!locOpen}
                  onToggle={() => setLocOpen((o) => !o)}
                  tenantName={p.tenant.name}
                  tile={tile}
                  onExpand={() => {
                    // §10 "Map tap-to-expand": the expand rate is what says whether the map earns its screen space.
                    track('map.expand', { kind: 'STILL' });
                    setMapOpen(true);
                  }}
                  onShowMap={tileWanted && lowData.on && !mapAsked ? () => setMapAsked(true) : null}
                  onHowTo={() => help.show('recovery')}
                  now={now}
                />
              ) : null}
              {!shot ? <FramingHintLine hint={framing} /> : null}
              {hint.text ? (
                <div className="vp-hint" role={hint.alert ? 'alert' : 'status'}>
                  {hint.good ? <span className="g"><Icon name="check" size={15} /></span> : null}
                  {hint.text}
                </div>
              ) : null}
              {/* A4: in the bottom stack's flow, under every other line — held sideways nothing can lie over Restart camera */}
              <CameraStalled cam={cam} />
            </div>
            {mapOpen && cardModel.kind === 'fix' && tile.status === 'ready' && tile.ref ? (
              <StampMapSheet fix={cardModel} centre={tile.ref} mosaic={mosaic} distanceWords={distanceWordsFor(t, cardModel.lines)} onClose={() => setMapOpen(false)} />
            ) : null}
          </div>
          {docGuide}
        </>
      )}

      <CaptureBar mode={decide ? 'decide' : 'shoot'} caption={caption}>
        {shot ? (
          !shot.verdict.ok || faceBlocked ? (
            <Button onClick={retake} data-testid="retake"><Icon name="redo" size={18} /> {t('sv.action.retake')}</Button>
          ) : sealing ? (
            <Button disabled>{t('sv.capture.queued')}</Button>
          ) : (
            <>
              <Button variant="soft" onClick={retake}><Icon name="redo" size={18} /> {t('sv.action.retake')}</Button>
              <Button onClick={() => void accept()} data-testid="use-this"><Icon name="check" size={18} /> {t('sv.action.useThis')}</Button>
            </>
          )
        ) : camBlocked ? (
          <Button onClick={turnOn}>{t('sv.action.allowed')}</Button>
        ) : cam.phase === 'primer' ? (
          <Button onClick={turnOn} data-testid="turn-on-camera">{t('sv.action.turnOnCamera')}</Button>
        ) : (
          <>
            <BarSlot side="l">
              {cam.multiCamera !== false ? (
                <BarButton icon="flip" label={t('sv.action.flipShort')} aria-label={t('sv.action.flipCamera')} onClick={flip} disabled={!live} data-testid="flip-camera" />
              ) : null}
            </BarSlot>
            <BarSlot side="c">
              <Shutter kind="photo" label={t('sv.action.takePhoto')} onClick={() => void capture()} disabled={!live || !cam.framesSeen || assessing} data-testid="take-photo" />
            </BarSlot>
          </>
        )}
      </CaptureBar>
    </div>
  );
}
